import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Clock, RefreshCw } from "lucide-react";
import { pairPoll, pairStart } from "../lib/deviceApi";

// The physical long edge (in device pixels) lets the server auto-select UHD for 4K panels.
function longEdgePx() {
  const dpr = window.devicePixelRatio || 1;
  const w = window.screen?.width || window.innerWidth;
  const h = window.screen?.height || window.innerHeight;
  return Math.round(Math.max(w, h) * dpr);
}

function mmss(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function TvPairing({ notice, onLinked }) {
  const [pair, setPair] = useState(null);
  const [status, setStatus] = useState("starting"); // starting | waiting | expired | denied | error
  const [secondsLeft, setSecondsLeft] = useState(0);
  const deviceCodeRef = useRef(null); // secret; NEVER rendered/logged/put in a URL
  const pollRef = useRef(null);

  const start = useCallback(async () => {
    setStatus("starting");
    const r = await pairStart({
      platform: "tv",
      name_suggestion: "Living Room TV",
      physical_long_edge_px: longEdgePx(),
      screen_dpr: window.devicePixelRatio || 1,
    });
    if (!r.ok || !r.data?.device_code) { setStatus("error"); return; }
    deviceCodeRef.current = r.data.device_code;
    setPair(r.data);
    setSecondsLeft(r.data.expires_in || 600);
    setStatus("waiting");
  }, []);

  useEffect(() => { start(); }, [start]);

  // Expiry countdown.
  useEffect(() => {
    if (status !== "waiting") return undefined;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [status, pair]);

  // Poll loop (honours slow_down; stops on approved/denied/expired).
  useEffect(() => {
    if (status !== "waiting" || !pair) return undefined;
    let stop = false;
    let interval = (pair.interval || 5) * 1000;
    const tick = async () => {
      if (stop) return;
      const r = await pairPoll(deviceCodeRef.current);
      if (stop) return;
      const st = r.data?.status;
      if (r.status === 410 || st === "expired") { setStatus("expired"); return; }
      if (st === "denied") { setStatus("denied"); return; }
      if (st === "approved" && r.data.device_token) {
        onLinked(r.data.device_token, r.data.device_id);
        return;
      }
      if (st === "slow_down") interval += 2000;
      pollRef.current = setTimeout(tick, interval);
    };
    pollRef.current = setTimeout(tick, interval);
    return () => { stop = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [status, pair, onLinked]);

  if (status === "expired" || status === "denied" || status === "error") {
    const copy = {
      expired: { h: "This code expired", p: "For your security the pairing code timed out. Generate a fresh code to link this TV." },
      denied: { h: "Linking was declined", p: "This request was declined on the phone. You can try again with a new code." },
      error: { h: "Something went wrong", p: "We couldn't reach World Wide Wallpaper. Check the TV's connection and try again." },
    }[status];
    return (
      <div className="tv-root tv-brandbg tv-center" data-testid={`tv-pair-${status}`}>
        <div className="tv-state-card">
          <h2>{copy.h}</h2>
          <p>{copy.p}</p>
          <button className="tv-btn" data-testid="tv-pair-retry" autoFocus onClick={start}>
            <RefreshCw size={18} style={{ verticalAlign: "-4px", marginRight: 8 }} /> Get a new code
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tv-pair" data-testid="tv-pairing">
      <div className="tv-pair-left">
        <div className="tv-brand">
          <img className="tv-emblem" src="/brand/www-emblem.png" alt=""
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="tv-wordmark">World Wide Wallpaper</span>
        </div>
        {notice && <div className="tv-notice" data-testid="tv-pair-notice">{notice}</div>}
        <h1 className="tv-pair-h1">Link this TV</h1>
        <p className="tv-pair-lead">A world of joy, quietly shared — as a slideshow on your screen.</p>
        <ol className="tv-steps">
          <li><span className="tv-step-num">1</span><span>On your phone, open <span className="tv-url">{pair ? pair.verification_uri.replace(/^https?:\/\//, "") : "…"}</span></span></li>
          <li><span className="tv-step-num">2</span><span>Sign in, then scan the code or enter it by hand</span></li>
          <li><span className="tv-step-num">3</span><span>Approve this TV — the slideshow starts automatically</span></li>
        </ol>
        <div>
          <div className="tv-code-sub">Pairing code</div>
          <div className="tv-code" data-testid="tv-user-code">{pair ? pair.user_code : "----"}</div>
        </div>
        {status === "waiting" && (
          <div className="tv-countdown" data-testid="tv-countdown">
            <Clock size={15} /> Waiting for your phone · code expires in {mmss(secondsLeft)}
          </div>
        )}
      </div>

      <div className="tv-pair-right">
        <div className="tv-qr-card" data-testid="tv-qr">
          {pair && (
            <QRCodeSVG
              value={pair.verification_uri_complete}
              size={260}
              level="M"
              marginSize={2}
              data-testid="tv-qr-svg"
            />
          )}
        </div>
        <div className="tv-qr-hint" data-testid="tv-qr-hint">
          Scan with your phone camera — no typing needed on the TV.
        </div>
      </div>
    </div>
  );
}
